import React, { useState } from 'react';
import { CreditCard, Wallet, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYMENT_CONFIG = {
  register: {
    monthly: {
      label: 'MENSUALIDAD',
      price: '$150 USD',
      priceAlt: '150 USDT',
      binance: {
        amount: '150 USDT',
        link: 'https://s.binance.com/SdubVheW',
        qr: '/binance_qr_monthly.png',
        nickname: 'Ricardopazfx',
      },
      card: {
        amount: '$150 USD',
        link: 'https://checkout.bold.co/payment/LNK_VUQ0I35S2L',
        label: 'Vas a pagar $150 USD a Alienígena.',
      },
    },
  },
  renewal: {
    monthly: {
      label: 'MENSUALIDAD',
      price: '$100 USD',
      priceAlt: '100 USDT',
      binance: {
        amount: '100 USDT',
        link: null,
        qr: '/binance_qr_renewal.jpg',
        nickname: 'Ricardopazfx',
      },
      card: null,
    },
  },
  annual: {
    label: 'ANUALIDAD',
    price: '$2.000.000',
    priceSuffix: 'COP',
    card: {
      amount: '$2.000.000 COP',
      link: 'https://checkout.bold.co/payment/LNK_J8Y8HQNF6E',
      label: 'Vas a pagar $2.000.000 COP a Alienígena para tu membresía anual.',
    },
  },
};

export function PaymentOptions({ title, subtitle, userName, userEmail, onBack, mode = 'register' }) {
  const [plan, setPlan] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleConfirmPayment = async () => {
    if (!plan) return;
    setConfirming(true);
    try {
      await axios.post(`${API}/payment-notify`, {
        plan_type: plan,
        user_name: userName || 'Usuario',
        user_email: userEmail || '',
      });
      setConfirmed(true);
      toast.success('Notificación enviada al administrador');
    } catch {
      toast.error('Error al enviar notificación');
    } finally {
      setConfirming(false);
    }
  };

  if (confirmed) {
    return (
      <div className="w-full max-w-lg mx-auto">
        <div className="glass-panel rounded-lg p-8 alien-glow text-center" data-testid="payment-confirmed">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-emerald-500" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-emerald-50 mb-3" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
            NOTIFICACIÓN ENVIADA
          </h2>
          <p className="text-emerald-100/70 mb-2">
            El administrador ha sido notificado de tu pago.
          </p>
          <p className="text-emerald-400 text-sm mb-6">
            Tu cuenta será activada una vez se verifique el pago.
          </p>
          {onBack && (
            <Button onClick={onBack} className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold uppercase tracking-wider">
              Ir a Iniciar Sesión
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Plan selection
  if (!plan) {
    const monthlyConfig = mode === 'renewal' ? PAYMENT_CONFIG.renewal.monthly : PAYMENT_CONFIG.register.monthly;
    const annualConfig = PAYMENT_CONFIG.annual;

    return (
      <div className="w-full max-w-lg mx-auto">
        <div className="glass-panel rounded-lg p-8 alien-glow" data-testid="payment-plan-select">
          <div className="flex justify-center mb-6">
            <img src="/logo.png" alt="Trading Alienígena" className="w-16 h-16 object-contain" />
          </div>
          <h2 className="text-2xl font-bold text-center mb-2 text-emerald-50" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
            {title || 'ELIGE TU PLAN'}
          </h2>
          <p className="text-center text-emerald-100/60 mb-8">{subtitle || 'Selecciona tu membresía para acceder a la plataforma'}</p>

          <div className="space-y-4">
            {/* Monthly */}
            <button
              onClick={() => setPlan('monthly')}
              className="w-full text-left bg-black/30 border-2 border-emerald-500/30 rounded-xl p-5 hover:border-emerald-500/70 transition-all group"
              data-testid="plan-monthly-btn"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-50 font-bold text-lg group-hover:text-emerald-400 transition-colors">MENSUALIDAD</p>
                  <p className="text-emerald-100/50 text-sm mt-1">Acceso completo por 1 mes</p>
                </div>
                <div className="text-right">
                  <p className="text-emerald-400 font-bold text-xl">{monthlyConfig.price}</p>
                  <p className="text-emerald-100/40 text-xs">o {monthlyConfig.priceAlt}</p>
                </div>
              </div>
            </button>

            {/* Annual */}
            <button
              onClick={() => setPlan('annual')}
              className="w-full text-left bg-[#F0B90B]/5 border-2 border-[#F0B90B]/30 rounded-xl p-5 hover:border-[#F0B90B]/70 transition-all group relative overflow-hidden"
              data-testid="plan-annual-btn"
            >
              <div className="absolute top-2 right-2 bg-[#F0B90B] text-black text-[10px] font-bold px-2 py-0.5 rounded">{mode === 'renewal' ? 'UPGRADE' : 'MEJOR PRECIO'}</div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-50 font-bold text-lg group-hover:text-[#F0B90B] transition-colors">ANUALIDAD</p>
                  <p className="text-emerald-100/50 text-sm mt-1">Acceso completo por 1 año</p>
                </div>
                <div className="text-right">
                  <p className="text-[#F0B90B] font-bold text-xl">{annualConfig.price}</p>
                  <p className="text-emerald-100/40 text-xs">{annualConfig.priceSuffix}</p>
                </div>
              </div>
            </button>
          </div>

          {onBack && (
            <div className="text-center mt-6">
              <button onClick={onBack} className="text-emerald-400/70 hover:text-emerald-400 text-sm flex items-center justify-center space-x-1 mx-auto">
                <ArrowLeft className="h-3 w-3" /><span>Volver</span>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Payment methods for selected plan
  const config = plan === 'annual'
    ? PAYMENT_CONFIG.annual
    : (mode === 'renewal' ? PAYMENT_CONFIG.renewal.monthly : PAYMENT_CONFIG.register.monthly);

  return (
    <div className="w-full max-w-lg mx-auto">
      <div className="glass-panel rounded-lg p-6 sm:p-8 alien-glow" data-testid="payment-methods">
        <div className="flex justify-center mb-4">
          <img src="/logo.png" alt="Trading Alienígena" className="w-12 h-12 object-contain" />
        </div>
        <h2 className="text-xl font-bold text-center mb-1 text-emerald-50" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
          {config.label}
        </h2>
        <p className="text-center text-emerald-100/50 text-sm mb-6">Elige tu método de pago</p>

        <div className="space-y-4">
          {/* Binance Pay (only for monthly) */}
          {config.binance && (
            <div className="bg-[#F0B90B]/10 border border-[#F0B90B]/30 rounded-xl p-5" data-testid="payment-binance">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-9 h-9 bg-[#F0B90B]/20 rounded-lg flex items-center justify-center">
                  <Wallet className="h-4 w-4 text-[#F0B90B]" />
                </div>
                <div>
                  <p className="text-[#F0B90B] font-bold">Pagar con Binance</p>
                  <p className="text-emerald-100/40 text-xs">{config.binance.amount}</p>
                </div>
              </div>
              <div className="flex justify-center mb-3">
                <img src={config.binance.qr} alt="Binance QR" className="w-48 rounded-lg" />
              </div>
              <p className="text-center text-emerald-100/50 text-xs mb-3">Escanea con la app de Binance</p>
              <p className="text-center text-[#F0B90B]/70 text-xs">Nickname: <span className="text-[#F0B90B] font-bold">{config.binance.nickname}</span></p>
              <a href={config.binance.link} target="_blank" rel="noopener noreferrer" className="block mt-3">
                <Button className="w-full bg-[#F0B90B] text-black hover:bg-[#F0B90B]/80 font-bold text-sm" data-testid="binance-link-btn">
                  Abrir enlace de Binance Pay
                </Button>
              </a>
            </div>
          )}

          {/* Card Payment */}
          <a href={config.card.link} target="_blank" rel="noopener noreferrer" className="block" data-testid="payment-card">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5 hover:border-emerald-500/50 transition-all cursor-pointer">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-9 h-9 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <CreditCard className="h-4 w-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-emerald-400 font-bold">Tarjeta Crédito / Débito</p>
                  <p className="text-emerald-100/40 text-xs">{config.card.amount}</p>
                </div>
              </div>
              <p className="text-emerald-100/50 text-xs">{config.card.label}</p>
            </div>
          </a>

          {/* Confirm payment button */}
          <div className="border-t border-emerald-500/20 pt-4">
            <Button
              onClick={handleConfirmPayment}
              disabled={confirming}
              className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold uppercase tracking-wider py-5"
              data-testid="confirm-payment-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {confirming ? 'Enviando...' : 'Ya realicé mi pago'}
            </Button>
            <p className="text-emerald-100/40 text-xs text-center mt-2">
              Al confirmar, el administrador será notificado para verificar y activar tu cuenta.
            </p>
          </div>
        </div>

        <div className="text-center mt-4">
          <button onClick={() => setPlan(null)} className="text-emerald-400/70 hover:text-emerald-400 text-sm flex items-center justify-center space-x-1 mx-auto">
            <ArrowLeft className="h-3 w-3" /><span>Cambiar plan</span>
          </button>
        </div>
      </div>
    </div>
  );
}
